<!-- pages/city/[city]/index.vue -->
<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  localBusinessNode,
  orgNode,
  breadcrumbNode,
} from "../../../composables/useSeo";
import { useLocations, humanize } from "~/composables/useLocations";
import { usePhoneNumber } from "../../../composables/usePhoneNumber";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();

const route = useRoute();
const cityParam = String(route.params.city || "")
  .trim()
  .toLowerCase();

// Get centralized data
const { cities, services } = useLocations();

if (!cities.includes(cityParam)) {
  throw createError({ statusCode: 404, statusMessage: "City Not Found" });
}

const cityHuman = humanize(cityParam);

// Helper function to get service icons
const getServiceIcon = (service: string) => {
  const icons: Record<string, string> = {
    "junk-removal": "🗑️",
    "appliance-removal": "🔌",
    "shed-demolition": "🏗️",
    "garage-cleanout": "🚗",
    "hot-tub-removal": "🛁",
    "construction-debris": "🏠",
    "furniture-removal": "🪑",
    "mattress-removal": "🛏️",
    "electronics-recycling": "💻",
    "estate-cleanout": "🏡",
    "office-cleanout": "🏢",
    "warehouse-cleanout": "🏭",
  };
  return icons[service] || "📦";
};

// SEO
useSeoBase({
  title: `${cityHuman} Junk Removal & Hauling Services | Junk Guys MN`,
  description: `Fast, affordable junk removal in ${cityHuman}, Minnesota. Same-day service available for furniture, appliances, construction debris, and more.`,
  canonical: `https://junkguysmn.com/city/${cityParam}`,
  ogImage: `https://junkguysmn.com/city/${cityParam}/og-image.jpg`,
});

// Breadcrumb schema
const crumbs = [
  { name: "Home", url: "https://junkguysmn.com" },
  { name: "Cities", url: "https://junkguysmn.com/services" },
  { name: cityHuman, url: `https://junkguysmn.com/city/${cityParam}` },
];

useJsonLd([orgNode(), localBusinessNode(), breadcrumbNode(crumbs)]);

// Neighborhoods data - expandable for more cities
const neighborhoods: Record<string, string[]> = {
  minneapolis: [
    "North Loop",
    "Northeast",
    "Uptown",
    "Longfellow",
    "Powderhorn",
    "Whittier",
    "Loring Park",
    "Downtown East",
    "Downtown West",
    "Marcy-Holmes",
    "Prospect Park",
    "Como",
    "Seward",
    "Phillips",
    "Central",
    "Bryant",
    "Kingfield",
    "Tangletown",
    "Fulton",
    "Kenny",
    "Armatage",
    "Linden Hills",
    "West Calhoun",
    "East Calhoun",
    "Lowry Hill",
    "Lowry Hill East",
    "Cedar-Isles-Dean",
    "Kenwood",
    "East Isles",
    "West Isles",
    "Harriet",
    "Diamond Lake",
    "Field",
    "Regina",
    "Nokomis",
    "Hiawatha",
    "Wenonah",
    "Morris Park",
    "Ventura Village",
    "Standish",
    "Ericsson",
    "Hale",
    "Page",
    "Dowling",
    "Folwell",
    "Webber-Camden",
    "Shingle Creek",
    "Lind-Bohanon",
    "Victory",
    "Cleveland",
    "Columbia Heights",
    "Audubon Park",
    "Windom",
    "Windom Park",
    "Marshall Terrace",
    "Bottineau",
    "St. Anthony West",
    "St. Anthony East",
    "St. Anthony Park",
    "Como Park",
    "Frogtown",
    "Summit-University",
    "West Seventh",
    "Dayton's Bluff",
    "Payne-Phalen",
    "Greater East Side",
    "Conway-Battle Creek",
    "Highwood",
    "Highland Park",
    "Mac-Groveland",
    "Summit Hill",
    "West Side",
    "Capitol Heights",
    "Capitol River",
    "Downtown",
    "Cathedral Hill",
    "West Seventh",
    "West Side",
    "Dayton's Bluff",
    "Payne-Phalen",
    "Greater East Side",
    "Conway-Battle Creek",
    "Highwood",
    "Highland Park",
    "Mac-Groveland",
    "Summit Hill",
    "West Side",
    "Capitol Heights",
    "Capitol River",
    "Downtown",
    "Cathedral Hill",
  ],
  "saint-paul": [
    "Highland Park",
    "Mac-Groveland",
    "Como Park",
    "Hamline-Midway",
    "Summit Hill",
    "West Seventh",
    "West Side",
    "Dayton's Bluff",
    "Payne-Phalen",
    "Greater East Side",
    "Conway-Battle Creek",
    "Highwood",
    "Capitol Heights",
    "Capitol River",
    "Downtown",
    "Cathedral Hill",
    "West Side",
    "Dayton's Bluff",
    "Payne-Phalen",
    "Greater East Side",
    "Conway-Battle Creek",
    "Highwood",
    "Capitol Heights",
    "Capitol River",
    "Downtown",
    "Cathedral Hill",
  ],
  bloomington: [
    "Northtown",
    "Southtown",
    "West Bloomington",
    "East Bloomington",
    "Central Bloomington",
    "Normandale",
    "Valley View",
    "Old Bloomington",
    "Penn American",
    "Oak Grove",
    "Oxboro",
    "Mall Area",
    "Airport Area",
  ],
  eagan: [
    "Central Eagan",
    "North Eagan",
    "South Eagan",
    "East Eagan",
    "West Eagan",
    "Cedar Point",
    "Eagan Highlands",
    "Eagan Woods",
    "Eagan Commons",
    "Eagan Market",
  ],
  "eden-prairie": [
    "Central Eden Prairie",
    "North Eden Prairie",
    "South Eden Prairie",
    "East Eden Prairie",
    "West Eden Prairie",
    "Eden Prairie Center",
    "Eden Prairie Mall",
    "Eden Prairie Town Center",
  ],
  edina: [
    "Edina",
    "Morningside",
    "Country Club",
    "Southdale",
    "Centennial Lakes",
    "Edina Galleria",
    "Edina Cinema",
    "Edina Country Club",
  ],
  richfield: [
    "Central Richfield",
    "North Richfield",
    "South Richfield",
    "East Richfield",
    "West Richfield",
    "Richfield Town Center",
    "Richfield Mall",
  ],
  burnsville: [
    "Central Burnsville",
    "North Burnsville",
    "South Burnsville",
    "East Burnsville",
    "West Burnsville",
    "Burnsville Center",
    "Burnsville Mall",
    "Burnsville Town Center",
  ],
  "apple-valley": [
    "Central Apple Valley",
    "North Apple Valley",
    "South Apple Valley",
    "East Apple Valley",
    "West Apple Valley",
    "Apple Valley Town Center",
    "Apple Valley Mall",
  ],
  lakeville: [
    "Central Lakeville",
    "North Lakeville",
    "South Lakeville",
    "East Lakeville",
    "West Lakeville",
    "Lakeville Town Center",
    "Lakeville Mall",
  ],
  rosemount: [
    "Central Rosemount",
    "North Rosemount",
    "South Rosemount",
    "East Rosemount",
    "West Rosemount",
    "Rosemount Town Center",
  ],
  savage: [
    "Central Savage",
    "North Savage",
    "South Savage",
    "East Savage",
    "West Savage",
    "Savage Town Center",
  ],
  shakopee: [
    "Central Shakopee",
    "North Shakopee",
    "South Shakopee",
    "East Shakopee",
    "West Shakopee",
    "Shakopee Town Center",
    "Shakopee Mall",
  ],
  "prior-lake": [
    "Central Prior Lake",
    "North Prior Lake",
    "South Prior Lake",
    "East Prior Lake",
    "West Prior Lake",
    "Prior Lake Town Center",
  ],
  woodbury: [
    "Central Woodbury",
    "North Woodbury",
    "South Woodbury",
    "East Woodbury",
    "West Woodbury",
    "Woodbury Town Center",
    "Woodbury Mall",
  ],
  "cottage-grove": [
    "Central Cottage Grove",
    "North Cottage Grove",
    "South Cottage Grove",
    "East Cottage Grove",
    "West Cottage Grove",
    "Cottage Grove Town Center",
  ],
  maplewood: [
    "Central Maplewood",
    "North Maplewood",
    "South Maplewood",
    "East Maplewood",
    "West Maplewood",
    "Maplewood Town Center",
    "Maplewood Mall",
  ],
  oakdale: [
    "Central Oakdale",
    "North Oakdale",
    "South Oakdale",
    "East Oakdale",
    "West Oakdale",
    "Oakdale Town Center",
  ],
  stillwater: [
    "Central Stillwater",
    "North Stillwater",
    "South Stillwater",
    "East Stillwater",
    "West Stillwater",
    "Stillwater Town Center",
    "Stillwater Mall",
  ],
  roseville: [
    "Central Roseville",
    "North Roseville",
    "South Roseville",
    "East Roseville",
    "West Roseville",
    "Roseville Town Center",
    "Roseville Mall",
  ],
  shoreview: [
    "Central Shoreview",
    "North Shoreview",
    "South Shoreview",
    "East Shoreview",
    "West Shoreview",
    "Shoreview Town Center",
  ],
  "white-bear-lake": [
    "Central White Bear Lake",
    "North White Bear Lake",
    "South White Bear Lake",
    "East White Bear Lake",
    "West White Bear Lake",
    "White Bear Lake Town Center",
    "White Bear Lake Mall",
  ],
  blaine: [
    "Central Blaine",
    "North Blaine",
    "South Blaine",
    "East Blaine",
    "West Blaine",
    "Blaine Town Center",
    "Blaine Mall",
  ],
  "coon-rapids": [
    "Central Coon Rapids",
    "North Coon Rapids",
    "South Coon Rapids",
    "East Coon Rapids",
    "West Coon Rapids",
    "Coon Rapids Town Center",
    "Coon Rapids Mall",
  ],
  andover: [
    "Central Andover",
    "North Andover",
    "South Andover",
    "East Andover",
    "West Andover",
    "Andover Town Center",
  ],
  "brooklyn-park": [
    "Central Brooklyn Park",
    "North Brooklyn Park",
    "South Brooklyn Park",
    "East Brooklyn Park",
    "West Brooklyn Park",
    "Brooklyn Park Town Center",
    "Brooklyn Park Mall",
  ],
  "brooklyn-center": [
    "Central Brooklyn Center",
    "North Brooklyn Center",
    "South Brooklyn Center",
    "East Brooklyn Center",
    "West Brooklyn Center",
    "Brooklyn Center Town Center",
  ],
  "maple-grove": [
    "Central Maple Grove",
    "North Maple Grove",
    "South Maple Grove",
    "East Maple Grove",
    "West Maple Grove",
    "Maple Grove Town Center",
    "Maple Grove Mall",
  ],
  plymouth: [
    "Central Plymouth",
    "North Plymouth",
    "South Plymouth",
    "East Plymouth",
    "West Plymouth",
    "Plymouth Town Center",
    "Plymouth Mall",
  ],
  minnetonka: [
    "Central Minnetonka",
    "North Minnetonka",
    "South Minnetonka",
    "East Minnetonka",
    "West Minnetonka",
    "Minnetonka Town Center",
    "Minnetonka Mall",
  ],
  hopkins: [
    "Central Hopkins",
    "North Hopkins",
    "South Hopkins",
    "East Hopkins",
    "West Hopkins",
    "Hopkins Town Center",
    "Hopkins Mall",
  ],
  "golden-valley": [
    "Central Golden Valley",
    "North Golden Valley",
    "South Golden Valley",
    "East Golden Valley",
    "West Golden Valley",
    "Golden Valley Town Center",
  ],
  "st-louis-park": [
    "Central St Louis Park",
    "North St Louis Park",
    "South St Louis Park",
    "East St Louis Park",
    "West St Louis Park",
    "St Louis Park Town Center",
    "St Louis Park Mall",
  ],
  wayzata: [
    "Central Wayzata",
    "North Wayzata",
    "South Wayzata",
    "East Wayzata",
    "West Wayzata",
    "Wayzata Town Center",
    "Wayzata Mall",
  ],
};

// Get neighborhoods for this city (limit to first 8 for display)
const cityNeighborhoods = neighborhoods[cityParam]?.slice(0, 8) || [];
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="max-w-4xl mx-auto text-center">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Junk Removal & Light Demolition in {{ cityHuman }}, MN
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed mb-8">
            We provide fast, full‑service junk removal and light demolition
            throughout {{ cityHuman }}
            and nearby neighborhoods. Transparent pricing, friendly crew, and
            responsible disposal.
          </p>
          <div class="flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Grid -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Services Available in {{ cityHuman }}
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            From small appliance removal to complete garage cleanouts, we handle
            every job with professionalism and care.
          </p>
        </div>

        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <NuxtLink
            v-for="service in services"
            :key="service"
            :to="`/city/${cityParam}/${service}`"
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-lg hover:border-primary-light transition-all duration-300 hover:-translate-y-1 group"
          >
            <div class="flex items-center justify-between mb-4">
              <h3
                class="text-xl font-bold text-slate-900 group-hover:text-primary-main transition-colors capitalize"
              >
                {{ (service as string).replace(/-/g, " ") }}
              </h3>
              <span
                class="text-2xl text-slate-400 group-hover:text-primary-main transition-colors"
                >→</span
              >
            </div>
            <p class="text-slate-600 text-sm">
              Professional {{ (service as string).replace(/-/g, " ") }} service
              in {{ cityHuman }} with transparent pricing.
            </p>
          </NuxtLink>
        </div>

        <div class="text-center mt-8">
          <p class="text-slate-600">
            Looking for costs? See our
            <NuxtLink
              to="/pricing"
              class="text-primary-main hover:text-primary-dark font-medium"
              >transparent pricing</NuxtLink
            >.
          </p>
        </div>
      </div>
    </section>

    <!-- Neighborhoods Section -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Neighborhoods We Serve
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            We cover {{ cityHuman }} and surrounding areas with reliable,
            same-day service.
          </p>
        </div>

        <div
          v-if="neighborhoods[cityParam]"
          class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto"
        >
          <div
            v-for="neighborhood in neighborhoods[cityParam]"
            :key="neighborhood"
            class="p-4 bg-slate-50 rounded-xl text-center text-slate-700 hover:bg-primary-main/5 transition-colors border border-slate-200 hover:border-primary-light"
          >
            {{ neighborhood }}
          </div>
        </div>
        <p v-else class="text-center text-lg text-slate-600">
          We serve {{ cityHuman }} and surrounding neighborhoods across the Twin
          Cities metro area.
        </p>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Simple, transparent process from start to finish
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">1</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Fast Estimate
            </h3>
            <p class="text-slate-600">
              Get a quick estimate by text or in‑person
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">2</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              We Handle Everything
            </h3>
            <p class="text-slate-600">
              On‑time arrival, careful removal, broom‑clean finish
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">3</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Responsible Disposal
            </h3>
            <p class="text-slate-600">
              Donation/recycling when possible; compliant disposal
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-4xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Get Started in {{ cityHuman }}?
        </h2>
        <p class="text-xl text-white mb-8">
          Get your junk removal done today. Fast, reliable, and eco-friendly
          service.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            @click="trackConversion"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call Now
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
